import { alpha, styled } from '@mui/material';

type ColorType =
  | 'transparent'
  | 'lightTransparent'
  | 'paper'
  | 'error'
  | 'default';
type PositionType = 'topLeft' | 'centerLeft' | 'centerRight' | 'bottomRight';

type StyledCircleProps = {
  size?: number;
  border?: number;
  color?: ColorType;
  position?: PositionType;
};

const StyledCircle = styled('div')<StyledCircleProps>(
  ({ theme, size, border, color, position }) => ({
    borderRadius: '50%',
    borderWidth: border,
    borderStyle: 'solid',
    position: 'absolute',
    width: size,
    height: size,
    zIndex: 1,

    // Color styles
    ...(color === 'paper' && {
      borderColor: theme.palette.background.paper,
    }),
    ...(color === 'transparent' && {
      borderColor: alpha(theme.palette.common.white, 0.75),
    }),
    ...(color === 'lightTransparent' && {
      borderColor: alpha(theme.palette.background.default, 0.2),
    }),
    ...(color === 'error' && {
      borderColor: theme.palette.error.main,
    }),
    ...(color === 'default' && {
      borderColor: theme.palette.background.default,
    }),

    // Position styles
    ...(position === 'topLeft' && {
      borderWidth: 16,
      width: 80,
      height: 80,
      left: 25,
      top: -40,
      [theme.breakpoints.up('sm')]: {
        borderWidth: 40,
        width: 220,
        height: 220,
        top: -150,
      },
      [theme.breakpoints.up('md')]: {
        borderWidth: 80,
        width: 430,
        height: 430,
        top: -250,
        left: '-15%',
      },
      [theme.breakpoints.up(1500)]: {
        left: '5%',
      },
    }),

    ...(position === 'bottomRight' && {
      borderWidth: 16,
      width: 80,
      height: 80,
      right: 25,
      bottom: -40,
      [theme.breakpoints.up('sm')]: {
        borderWidth: 40,
        width: 220,
        height: 220,
        bottom: -150,
      },
      [theme.breakpoints.up('md')]: {
        borderWidth: 80,
        width: 430,
        height: 430,
        bottom: -250,
        right: '-15%',
      },
      [theme.breakpoints.up(1500)]: {
        right: '5%',
      },
    }),

    ...(position === 'centerLeft' && {
      borderWidth: 16,
      width: 80,
      height: 80,
      left: -40,
      maxWidth: 'revert',
      top: '40%',
      [theme.breakpoints.up('sm')]: {
        borderWidth: 40,
        width: 220,
        height: 220,
        transform: 'translate(0, -50%)',
        top: '50%',
        left: -140,
      },
      [theme.breakpoints.up('md')]: {
        borderWidth: 70,
        width: 340,
        height: 340,
        left: -180,
      },
    }),

    ...(position === 'centerRight' && {
      borderWidth: 16,
      width: 80,
      height: 80,
      right: -40,
      top: '40%',
      [theme.breakpoints.up('sm')]: {
        borderWidth: 40,
        width: 220,
        height: 220,
        transform: 'translate(0, -50%)',
        top: '50%',
        right: -140,
      },
      [theme.breakpoints.up('md')]: {
        borderWidth: 70,
        width: 340,
        height: 340,
        right: -180,
      },
    }),
  }),
);

export type CircleProps = React.ComponentProps<typeof StyledCircle>;

const Circle: React.FC<CircleProps> = ({
  color = 'transparent',
  size = 580,
  border = 100,
  ...props
}) => {
  return <StyledCircle size={size} border={border} color={color} {...props} />;
};

export default Circle;
